// src/v2/components/LeanLensSwitcher.jsx
//
// MP-22: Lean animated-underline lens switcher.
//
// A text-only tab switcher with a 2.5px animated underline bar that slides
// to the active label on click (~0.3s ease). No container box, no border,
// no background fill on tabs — so it never competes visually with the
// toolbar controls above it.
//
// Props:
//   tabs      — array of { key, label, desc?, testid? }
//   value     — currently-active key
//   onChange  — called with the clicked key
//   ariaLabel — string; sets the aria-label on the tablist element
//
// Below the switcher row, a one-line description renders the active tab's
// label + desc: "<b>Plan</b> · Set dispositions".

import React, { useRef, useState, useLayoutEffect } from "react";
import { T } from "../../app/tokens.js";

export function LeanLensSwitcher({ tabs, value, onChange, ariaLabel = "View" }) {
  const labelRefs = useRef([]);
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });
  // Track whether the indicator has been positioned at least once.
  // On first render, skip the CSS transition so the bar doesn't animate
  // from (0,0) to its real position.
  const initialised = useRef(false);

  useLayoutEffect(() => {
    const activeIdx = tabs.findIndex((t) => t.key === value);
    if (activeIdx < 0 || !labelRefs.current[activeIdx]) return;
    const el = labelRefs.current[activeIdx];
    setIndicator({ left: el.offsetLeft, width: el.offsetWidth });
    initialised.current = true;
  }, [value, tabs]);

  const activeTab = tabs.find((t) => t.key === value);

  return (
    <>
      {/* Switcher row */}
      <div
        role="tablist"
        aria-label={ariaLabel}
        style={{
          position:    "relative",
          display:     "inline-flex",
          gap:         30,
          paddingBottom: 11,
        }}
      >
        {tabs.map((tab, i) => {
          const isActive = tab.key === value;
          return (
            <span
              key={tab.key}
              ref={(el) => { labelRefs.current[i] = el; }}
              role="tab"
              aria-selected={isActive}
              data-testid={tab.testid}
              onClick={() => onChange(tab.key)}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.color = T.ink ?? T.dark;
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.color = T.mid;
              }}
              style={{
                fontSize:   T.fsMd5,
                fontWeight: isActive ? T.fwBold : T.fwSemibold,
                color:      isActive ? T.accent : T.mid,
                cursor:     "pointer",
                userSelect: "none",
                whiteSpace: "nowrap",
                padding:    "2px 1px",
                transition: "color 0.22s ease",
              }}
            >
              {tab.label}
            </span>
          );
        })}

        {/* Animated underline indicator */}
        <div
          aria-hidden="true"
          style={{
            position:     "absolute",
            bottom:       0,
            left:         indicator.left,
            width:        indicator.width,
            height:       2.5,
            background:   T.accent,
            borderRadius: 3,
            transition:   initialised.current
              ? "left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1)"
              : "none",
          }}
        />
      </div>

    </>
  );
}
